import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useHistory, withRouter } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Popup } from 'semantic-ui-react';
import { ErrorHandler, withTranslation } from '@playbookux/playbook-web-core';
import withLayout from '../../../core/components/with-layout';
// utils
import {
  renderValue,
  formatCountry,
  formatState,
  formatLanguage,
  formatJobCategory,
  formatDate,
  formatWorkStatus,
  formatSmartphone,
  formatComputer,
  formatTablet,
} from './utils';
// styles
import {
  Container, ContainerLoader, Content, DataContainer, DataLabel, DataValue,
  Heading, SubHeading, ButtonContainer, EditButton, Loader,
  DisabledButton,
} from './styles';
// graphql
import { GET_PROFILE_ATTRIBUTES, LAST_EDITION } from './graphql';

function ProfileSummary({ me: { user_id: userId }, location, phrases }) {
  const history = useHistory();

  const { data, error, loading } = useQuery(GET_PROFILE_ATTRIBUTES, {
    variables: {
      userId,
    },
  });

  const {
    data: { profileEditedLastThreeMonths = {} } = {},
  } = useQuery(LAST_EDITION);

  const handleClick = () => {
    history.push('/profile', { prevPage: location });
  };

  if (loading) {
    return (
      <ContainerLoader>
        <Loader data-loader="profile-summary-loader" />
      </ContainerLoader>
    );
  }

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Error while retrieving tester profile."
      />
    );
  }

  const userData = data.user && data.user.profile;
  const currentPosition = userData && userData.currentPosition ? userData.currentPosition : {};
  const company = currentPosition && currentPosition.company ? currentPosition.company : {};

  return (
    <Container>
      <Heading>
        We&apos;ll use your profile information to match you with tests.
      </Heading>

      <SubHeading>
        Estimated time to complete: 2 minutes
      </SubHeading>

      {(profileEditedLastThreeMonths && (
        <ButtonContainer>
          <Popup
            trigger={<DisabledButton>Edit my profile</DisabledButton>}
            content="You’ve edited your profile too many times. You can make changes to your profile once
            every three months. Please contact hello@playbookux.com if you need to make additional changes."
            position="bottom center"
          />
        </ButtonContainer>
      )) || (
        <ButtonContainer>
          <EditButton onClick={handleClick}>Edit my profile</EditButton>
        </ButtonContainer>
      )}

      <Content>
        <DataContainer>
          <DataLabel>
            {phrases.scheduler_confirm_email}
          </DataLabel>
          <DataValue>
            {renderValue(userData.email)}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Birth Date
          </DataLabel>
          <DataValue>
            {formatDate(renderValue(userData.birthdate))}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Gender
          </DataLabel>
          <DataValue>
            {renderValue(userData.gender)}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Ethnicity
          </DataLabel>
          <DataValue>
            {renderValue(userData.raceEthnicHeritage)}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Employment Status
          </DataLabel>
          <DataValue>
            {formatWorkStatus(renderValue(userData.workStatus))}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Annual household income (in USD)
          </DataLabel>
          <DataValue>
            {renderValue(userData.annualHouseholdIncome)}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Job Title
          </DataLabel>
          <DataValue>
            {renderValue(currentPosition.title)}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Company Size
          </DataLabel>
          <DataValue>
            {renderValue(company.size)}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Industry
          </DataLabel>
          <DataValue>
            {renderValue(company.industry)}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Seniority
          </DataLabel>
          <DataValue>
            {renderValue(currentPosition.seniority)}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Education
          </DataLabel>
          <DataValue>
            {renderValue(userData.educationLevel)}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Languages
          </DataLabel>
          <DataValue>
            {formatLanguage(renderValue(userData.languages))}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Smartphone
          </DataLabel>
          <DataValue>
            {formatSmartphone(renderValue(userData.smartphone))}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Computer
          </DataLabel>
          <DataValue>
            {formatComputer(renderValue(userData.computer))}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Tablet
          </DataLabel>
          <DataValue>
            {formatTablet(renderValue(userData.tablet))}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            Country
          </DataLabel>
          <DataValue>
            {formatCountry(renderValue(userData.country))}
          </DataValue>
        </DataContainer>

        <DataContainer>
          <DataLabel>
            State
          </DataLabel>
          <DataValue>
            {formatState(renderValue(userData.state))}
          </DataValue>
        </DataContainer>

        <DataContainer last>
          <DataLabel>
            Job Roles
          </DataLabel>
          <DataValue>
            {formatJobCategory(renderValue(userData.jobCategories))}
          </DataValue>
        </DataContainer>
      </Content>
    </Container>
  );
}

ProfileSummary.propTypes = {
  me: PropTypes.shape({
    user_id: PropTypes.string,
  }).isRequired,
  location: PropTypes.shape().isRequired,
  phrases: PropTypes.shape({
    scheduler_confirm_email: PropTypes.string,
  }).isRequired,
};

export default compose(
  withTranslation,
  withLayout({ selectedTab: 'My Profile' }),
  withRouter,
)(ProfileSummary);
